import { SelectChangeEvent } from '@mui/material';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { isEqual } from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';
import { DropResult, DragStart } from 'react-beautiful-dnd';
import { useTranslation } from 'react-i18next';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useSnapshot } from 'valtio';
import useTacticKanbanGrouping from './hooks/useTacticKanbanGrouping.hook';
import useTacticKanbanMutations from './hooks/useTacticKanbanMutations.hook';
import useTacticKanbanStepActions from './hooks/useTacticKanbanStepActions.hook';
import { isTacticGroupingImprovementsAvailable } from './TacticKanban.utils';
import { mayaCepTemplateListRoute, mayaTacticPrintRoute } from 'basics/constants/routes.constants';
import { CepStatus, TacticMode, TacticStatus } from 'basics/enums/maya.enums';
import useGetAllTacticsQuery from 'basics/graphql/queries/getAllTactics';
import useGetCepByIdQuery from 'basics/graphql/queries/getCepById';
import { CepStateType } from 'basics/types/maya.types';
import { Channel } from 'generated/maya.types';
import cepState from 'states/cep.states';
import tacticState from 'states/tactic.states';

const useTacticKanban = () => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const cepStateValue = useSnapshot(cepState) as CepStateType;
  const cepId = cepStateValue.cep?._id || searchParams.get('id');
  const cepCreatedAt = cepStateValue.cep?.createdAt;
  const isCepApproved = cepStateValue.cep?.status === CepStatus.APPROVED;
  const now = new Date();
  const cepStartDate = new Date(cepStateValue.cep?.dateStart);
  const todayUTC = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
  const isCepStarted = cepStartDate <= todayUTC;
  const isApprovedStartedCep = isCepApproved && isCepStarted;
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [reorderingInProgress, setReorderingInProgress] = useState(false);
  const reorderingInProgressRef = useRef(false);
  const [isDraggingTactic, setIsDraggingTactic] = useState(false);

  const setReorderingInProgressRef = (value: boolean) => {
    reorderingInProgressRef.current = value;
  };

  const { data: customerEngagementPlanning, loading: cepLoading } = useGetCepByIdQuery(cepStateValue.cep?._id ? null : cepId);

  useEffect(() => {
    if (!cepLoading && cepStateValue?.cep?._id) {
      if (customerEngagementPlanning) {
        cepState.cep = customerEngagementPlanning;
      }
    }
  }, [customerEngagementPlanning, cepLoading, cepStateValue?.cep?._id]);

  const queryFilters = useMemo(
    () => ({
      cep: cepId,
      status: [TacticStatus.EMPTY, TacticStatus.DRAFT, TacticStatus.APPROVED],
    }),
    [cepId],
  );

  const { data, loading, refetch: refetchAllTactics } = useGetAllTacticsQuery(undefined, queryFilters);
  const flags = useFlags();

  const isCepTemplateRoute = pathname.startsWith(mayaCepTemplateListRoute);
  const sendToVeeva = !isCepTemplateRoute;
  const isApprovedCEP = cepStateValue.cep?.status === CepStatus.APPROVED;
  const isEditingEnabled = !isApprovedCEP || (isApprovedCEP
      && flags.mayaBdcfcsd653ExtendEditingOfApprovedCep
      && flags.mayaBdcfcsd1251ExtendTacticEditingOfApprovedCep);

  const { enabled, dateAccepted } = flags.mayaAkma777EnableTacticGroupingImprovementsJson || {};

  const isTacticGroupingImprovementsEnabled = isTacticGroupingImprovementsAvailable({
    enabled,
    dateAccepted,
    cepCreatedAt,
  });

  const shouldRestrictNewGroupCreationForApprovedStartedCep = Boolean(
    flags.mayaAkma1392RestrictNewGroupCreationForApprovedAndStartedCep && isApprovedStartedCep,
  );

  const shouldRestrictAddingToExistingGroupForApprovedStartedCep = Boolean(
    flags.mayaAkma1392RestrictAddingToExistingGroupForApprovedAndStartedCep && isApprovedStartedCep,
  );

  useEffect(() => {
    if (!loading && data && !reorderingInProgressRef.current) {
      const newTactics = data.filter((tactic) => tactic.channel !== Channel.group);
      const newTacticGroups = data.filter((tactic) => tactic.channel === Channel.group);

      if (!isEqual(newTactics, cepState.tactics)) {
        cepState.tactics = newTactics;
      }
      if (!isEqual(newTacticGroups, cepState.tacticGroups)) {
        cepState.tacticGroups = newTacticGroups;
      }
    }
  }, [data, loading, reorderingInProgress]);

  const {
    groupedTactics,
    paginatedGroupedTactics,
    idToStepMap,
    stepKeys,
  } = useTacticKanbanGrouping({
    tactics: cepStateValue.tactics,
    page,
    pageSize,
  });

  const hasMultipleSteps = Object.keys(groupedTactics).length > 1;
  const hasOnlySingleTacticSteps = Object.values(groupedTactics)
    .every((tactics) => tactics.length === 1);

  const canReorderStepsInApprovedStartedCep = isApprovedStartedCep
      && hasMultipleSteps;

  const canDragSingleTacticAsStepReorderInApprovedStartedCep = canReorderStepsInApprovedStartedCep
      && hasOnlySingleTacticSteps;

  useEffect(() => {
    const lastAvailablePage = Math.max(Math.ceil(stepKeys.length / pageSize) - 1, 0);

    if (page > lastAvailablePage) {
      setPage(lastAvailablePage);
    }
  }, [page, pageSize, stepKeys.length]);

  const {
    updateGroupRelations,
    syncAfterMutations,
    handleTacticGroupCreation,
  } = useTacticKanbanMutations({
    cepId,
    sendToVeeva,
    isApprovedCEP,
    refetchAllTactics,
  });

  const {
    reindexAfterDeletion,
    moveTacticToStep,
    createStepFromTactic,
    createStepFromTacticAtStart,
    reorderSteps,
    setStepMandatory,
    setStepDependency,
  } = useTacticKanbanStepActions({
    groupedTactics,
    idToStepMap,
    shouldRestrictNewGroupCreationForApprovedStartedCep,
    shouldRestrictAddingToExistingGroupForApprovedStartedCep,
    setReorderingInProgress,
    setReorderingInProgressRef,
    updateGroupRelations,
    syncAfterMutations,
    handleTacticGroupCreation,
  });

  const isGroupedTacticLockedInApprovedCep = (tacticId: string) => {
    const tacticToCheck = cepStateValue.tactics.find((tactic) => tactic._id === tacticId);
    const tacticGroupId = tacticToCheck?.parentTactic?._id;

    if (!isCepApproved || !tacticGroupId) {
      return false;
    }

    const tacticsInSameGroupCount = cepStateValue.tactics
      .filter((tactic) => tactic.parentTactic?._id === tacticGroupId)
      .length;

    return tacticsInSameGroupCount <= 2;
  };

  const onDragEnd = async (result: DropResult) => {
    try {
      const { destination, source, type, draggableId } = result;

      if (!destination) {
        return;
      }

      if (destination.droppableId === source.droppableId && destination.index === source.index) {
        return;
      }

      if (type === 'Step' && isTacticGroupingImprovementsEnabled) {
        await reorderSteps(source.index, destination.index);
        return;
      }

      if (type === 'Tactic' && isTacticGroupingImprovementsEnabled) {
        if (isGroupedTacticLockedInApprovedCep(draggableId)) {
          return;
        }

        if (destination.droppableId === source.droppableId) {
          return;
        }

        if (destination.droppableId === 'create-step-at-start') {
          await createStepFromTacticAtStart(draggableId);
          return;
        }

        if (destination.droppableId.startsWith('create-step-after-')) {
          await createStepFromTactic(draggableId, destination.droppableId);
          return;
        }

        await moveTacticToStep(draggableId, destination.droppableId, destination.index);
      }
    } finally {
      setIsDraggingTactic(false);
    }
  };

  const onDragStart = (start: DragStart) => {
    const { type } = start;

    if (type === 'Tactic' && isTacticGroupingImprovementsEnabled) {
      setIsDraggingTactic(true);
    }
  };

  const onPageChange = (event: unknown, newPage: number) => {
    setPage(newPage - 1);
  };

  const onPageSizeChange = (event: SelectChangeEvent<unknown>) => {
    setPageSize(event.target.value as number);
    setPage(0);
  };

  const newTacticClick = () => {
    tacticState.mode = TacticMode.form;
  };

  const newElementButton = {
    newElementLabel: t('maya_list_new_element_label', { elementTitle: 'Tactic' }),
    newElementClick: newTacticClick,
    newElementDisabled: isCepApproved
        && (!flags.mayaBdcfcsd653ExtendEditingOfApprovedCep || !flags.mayaBdcfcsd1251EnableTacticCreationForApprovedCep),
  };

  const printViewButton = {
    printViewLabel: t('maya_tactic_print_label'),
    printViewClick: () => window.open(`${mayaTacticPrintRoute}?id=${cepId}`, '_blank'),
  };

  return {
    allGroupedTactics: groupedTactics,
    groupedTactics: paginatedGroupedTactics,
    newElementButton,
    printViewButton,
    onDragEnd,
    onDragStart,
    loading,
    pagination: {
      page,
      pageSize,
      onPageChange,
      onPageSizeChange,
      count: stepKeys.length,
    },
    reorderSteps,
    createStepFromTactic,
    moveTacticToStep,
    createStepFromTacticAtStart,
    setStepDependency,
    setStepMandatory,
    reorderingInProgress,
    idToStepMap,
    isTacticGroupingImprovementsEnabled,
    isEditingEnabled,
    isDraggingTactic,
    reindexAfterDeletion,
    isCepApproved,
    isApprovedStartedCep,
    canReorderStepsInApprovedStartedCep,
    canDragSingleTacticAsStepReorderInApprovedStartedCep,
  };
};

export default useTacticKanban;
